import '../style.scss';
import './style.scss';
import { Button, ButtonFill, ButtonSize, ButtonType } from 'views/mutual/button';
import { Caption } from 'views/mutual/caption';
import { CorporateAccountOpenPages, useCorporateAccountOpenPageTypeStore } from 'views/pages/accountOpen/corporate/hooks';
import { InputTemplate } from 'views/templates/module/inputTemplate';
import { UnitInput, UnitInputPosition } from 'views/templates/module/unitInput';
import { AddressInput } from 'views/templates/module/addressInput';
import { FormContentHeader, FormType } from 'views/templates/module/contentHeader';
import { Selector } from 'views/templates/module/selector';
import { CorporateAddressConfirmSelectType, useCorporateInfo } from './hooks';
import { MultiSelections } from 'views/mutual/inputRadio';
import { CheckInput, AlignType } from 'views/templates/module/checkInput';
import { TemplateProps } from '../common';
import { CorporateBusinessContentStrs, SettlementDayStrs, SettlementMonthStrs } from 'views/utils/enums';
import { UnitSelector } from 'views/templates/module/unitSelector';
import { DateInput } from 'views/mutual/dateInput';
import { useCustomLocationName } from 'views/mutual/location/hooks';

export type CorporateInfoProps = {
  formik: any;
  corporateNumberProps: any;  
  corporateNameProps: any;
  corporateFuriganaProps : any;
  corporateAddressPorps: any;
  corporateAddressConfirmProps: any;
  registeredCorporateAddressPorps: any;
  corporateMainBusinessContentProps: any;
  corporateSettlementMonthProps: any;
  corporateSettlementDayProps: any;
  corporateEstablishmentDateProps: any;
  corporateCapitalProps: any;
  corporateNumberOfEmployeesProps: any;
  corporateHomepageProps: any;
  electronicDocumentAgreementProps: any;
};

export const CorporateInfoWrapper = () => {
  return <CorporateInfo {...useCorporateInfo({ isUpdate: false })} />;
};

export const CorporateInfo = ({
  formik,
  corporateNumberProps,
  corporateNameProps,
  corporateFuriganaProps,
  corporateAddressPorps,
  corporateAddressConfirmProps,
  registeredCorporateAddressPorps,
  corporateMainBusinessContentProps,
  corporateSettlementMonthProps,
  corporateSettlementDayProps,
  corporateEstablishmentDateProps,
  corporateCapitalProps,
  corporateNumberOfEmployeesProps,
  corporateHomepageProps,
  electronicDocumentAgreementProps,
  isUpdate,
}: CorporateInfoProps & TemplateProps) => {
  const { setAccountOpenPageType } = useCorporateAccountOpenPageTypeStore();

  useCustomLocationName('口座開設申込みフォーム');

  return (
    <form onSubmit={formik.handleSubmit} className="accountOpenTemplate">
      {!isUpdate && (
        <>
          <Caption label="法人情報" />
          <div>以降のページには、取引担当者様（代表者様が取引担当者様の場合は代表者様）がご入力ください。</div>
        </>
      )}
      <div className="accountOpenTemplate_input">
        <FormContentHeader label="法人番号" formType={FormType.Required} />
        <div className="accountOpenTemplate_input_wrapper">
          <div>
          国税庁法人番号公表サイトは
          <a 
          className="accountOpenTemplate_input_wrapper_link"
          href={process.env.REACT_APP_SEARCH_HOUJIN_NUMBER_URL} 
          target="_blank"
          rel="noopener noreferrer"
          >
            こちら
          </a>
          </div>
          <InputTemplate placeholder="例：3010001086522" maxLength={13} {...corporateNumberProps} />
        </div>

        <FormContentHeader label="法人名" formType={FormType.Required} />
        <div className="accountOpenTemplate_input_wrapper">
          <InputTemplate type="text" placeholder="例：株式会社FPG" {...corporateNameProps} />
        </div>

        <FormContentHeader label="法人名（フリガナ）" formType={FormType.Required} />
        <div className="accountOpenTemplate_input_wrapper">
          <InputTemplate type="text" placeholder="例：カブシキガイシャ エフピージー" {...corporateFuriganaProps} />
        </div>

        <FormContentHeader label="法人住所" formType={FormType.Required} />
        <div className="accountOpenTemplate_input_wrapper">
          <AddressInput name="corporate_address" {...corporateAddressPorps} />
        </div>

        <FormContentHeader label="法人住所の確認" formType={FormType.Required} />
        <div className="accountOpenTemplate_input_check">
          <MultiSelections
            items={[
              {
                label: '登記上の住所（本店・主たる事務所/支店・従たる事務所）を入力しました',
                value: CorporateAddressConfirmSelectType.Same,
              },
              { label: '登記上の住所以外の住所を入力しました', value: CorporateAddressConfirmSelectType.Different },
            ]}
            name="ConfirmAddress"
            {...corporateAddressConfirmProps}
          />
        </div>

        {formik.values.corporateAddressConfirm === CorporateAddressConfirmSelectType.Different && (
          <>
            <FormContentHeader label="登記上の法人住所" formType={FormType.Required} conditional={true} />
            <div className="accountOpenTemplate_input_wrapper">
              <AddressInput name="registered_corporate_address" {...registeredCorporateAddressPorps} />
            </div>
          </>
        )}
        <FormContentHeader label="決算日" formType={FormType.Required} />
        <div className="accountOpenTemplate_input_wrapper settlementMonthAndDate">
          <UnitSelector
            label="月"
            unit={UnitInputPosition.before}
            options={SettlementMonthStrs}
            {...corporateSettlementMonthProps}
          />
          <UnitSelector
            label="日"
            unit={UnitInputPosition.before}
            options={SettlementDayStrs}
            {...corporateSettlementDayProps}
          />
        </div>

        <FormContentHeader label="設立年月日" formType={FormType.Required} />
        <div className="accountOpenTemplate_input_wrapper">
          <DateInput placeholder={'年/月/日'} showYearMonthDropdown={true} {...corporateEstablishmentDateProps} />
        </div>

        <FormContentHeader label="事業内容" formType={FormType.Required} />
        <div className="accountOpenTemplate_input_wrapper">
          <Selector options={CorporateBusinessContentStrs} {...corporateMainBusinessContentProps} />
          {corporateMainBusinessContentProps.otherText && (
            <InputTemplate
              type="text"
              placeholder="その他を選択された場合は内容を具体的にご記入ください"
              {...corporateMainBusinessContentProps.otherText}
            />
          )}
        </div>

        <FormContentHeader label="資本金" formType={FormType.Required} />
        <div className="accountOpenTemplate_input_wrapper">
          <UnitInput
            label="円"
            unit={UnitInputPosition.after}
            placeholder="例：1000000000（カンマは入力不要です）"
            maxLength={11}
            {...corporateCapitalProps}
          />
        </div>

        <FormContentHeader label="従業員数" formType={FormType.Required} />
        <div className="accountOpenTemplate_input_wrapper">
          <UnitInput
            label="人"
            unit={UnitInputPosition.after}
            placeholder="例：5000（カンマは入力不要です）"
            maxLength={5}
            {...corporateNumberOfEmployeesProps}
          />
        </div>

        <FormContentHeader label="事業内容が確認できるホームページ" formType={FormType.Optional} />
        <div className="accountOpenTemplate_input_wrapper">
          <InputTemplate type="text" placeholder="例：https://www.fpg.jp/" {...corporateHomepageProps} />
        </div>

        {!isUpdate && (
          <>
            <FormContentHeader label="電磁的方法による提供の同意・承諾" formType={FormType.Required} />
            <div className="accountOpenTemplate_input_wrapper">
              <CheckInput
                name="electronic_document_agreement"
                label={
                  <>
                    <a className="accountOpenTemplate_input_wrapper_link"
                        href={process.env.REACT_APP_ELECTORIC_GENERAL_TERMS_CONDITION_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                      {'FPGオンライン取引・電子署名取引一般規約 '}
                    </a>
                    {'第5条を確認し、重要事項説明書の交付、契約締結前の情報の提供、契約締結時の情報の提供等の法令で定められた書面の交付、情報提供等を、以下の電磁的方法のうち、当該書面に記載すべき事項の提供又は情報提供等の方法として法令において認められる方法により受けること（電子交付）に承諾します。電子交付に代えて書面の交付を希望する場合にはログイン後に問い合わせフォームから申し出ます。'}
                    <br/>
                    <br/>
                    {'・電子メールを使用する方法'}
                    <br/>
                    {'・弊社のウェブページからダウンロードする方法'}
                    <br/>
                    {'・弊社のウェブページに備えられた顧客ファイルを利用する方法'}
                    <br/>
                    {'・弊社のウェブページで閲覧に供する方法'}
                    <br/>
                    <br/>
                    {'※電子交付を受けるには、Acrobat Readerの最新バージョン等が必要です。'}
                  </>
                }
                align={ AlignType.start }
                {...electronicDocumentAgreementProps}
              />
            </div>
          </>
        )}
      </div>

      {!isUpdate && (
        <div className="accountOpenTemplate_button">
          <Button type={ButtonType.Submit} label="次へ" buttonFill={ButtonFill.black} buttonSize={ButtonSize.medium} />
          <Button
            label="前の画面に戻る"
            buttonFill={ButtonFill.link}
            buttonSize={ButtonSize.small}
            onClick={() => setAccountOpenPageType(CorporateAccountOpenPages.advanceQuestion)}
          />
        </div>
      )}
    </form>
  );
};
